/**
 * Step 2 of the funnel — account + draft (CLAUDE.md's Applications section).
 *
 * Posts to `POST /api/applicant-sign-up`, the one unauthenticated
 * account-creation path in the app (see its route and `lib/auth.ts`'s
 * header on the API side). The response sets the session cookie directly —
 * there is no separate sign-in step here, unlike LoginPage — so this only
 * has to call `refresh()` to pick that up, then create the DRAFT
 * application and land on it.
 *
 * `unitId` comes from the URL (`?unit=<id>`), since there is no public
 * listings site yet (step 11) to pick one from inside this flow — a staff
 * member or an external listing (Zillow, a phone call) is what hands an
 * applicant this link today.
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, ApiError } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import { Field, FormErrors, PasswordInput, TextInput, fieldErrors } from '../../components/Form.js';
import { ApplyProgress } from './ApplyProgress.js';

export function ApplicantSignUp() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [searchParams] = useSearchParams();
  const unitId = searchParams.get('unit') ?? '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (!unitId) {
      setError(
        new ApiError(
          422,
          'VALIDATION',
          'This link is missing which unit to apply for. Contact the management office for a valid link.',
        ),
      );
      return;
    }

    setBusy(true);
    try {
      // The server validates with the same schema this form's fields map
      // onto (applicantSignUpSchema, packages/shared) — a 422 comes back
      // with per-field messages that fieldErrors() below renders inline,
      // same as every other form in this codebase.
      await api('/applicant-sign-up', {
        method: 'POST',
        body: JSON.stringify({ firstName, lastName, email, phone: phone || undefined, password }),
      });
      await refresh();

      const application = await api<{ id: string }>('/applications', {
        method: 'POST',
        body: JSON.stringify({ unitId }),
      });

      void navigate(`/apply/${application.id}`, { replace: true });
    } catch (caught) {
      setError(caught);
      setBusy(false);
    }
  }

  const errors = fieldErrors(error);

  return (
    <section>
      <ApplyProgress step={1} />

      <div className="apply-hero">
        <p className="apply__eyebrow">Create your account</p>
        <h1>Let's get you moved in.</h1>
        <p className="apply__lede">
          You'll use this to save your progress — close the tab any time and pick up right where you
          left off.
        </p>
      </div>

      <div className="card">
        <FormErrors error={error} />

        <form className="stack" onSubmit={(event) => void onSubmit(event)} noValidate>
          <Field label="First name" name="firstName" error={errors.firstName} required>
            <TextInput name="firstName" value={firstName} onChange={setFirstName} />
          </Field>
          <Field label="Last name" name="lastName" error={errors.lastName} required>
            <TextInput name="lastName" value={lastName} onChange={setLastName} />
          </Field>
          <Field label="Email" name="email" error={errors.email} required>
            <TextInput name="email" type="email" value={email} onChange={setEmail} />
          </Field>
          <Field label="Phone" name="phone" error={errors.phone} hint="Optional">
            <TextInput name="phone" type="tel" value={phone} onChange={setPhone} />
          </Field>
          <Field
            label="Password"
            name="password"
            error={errors.password}
            hint="At least 12 characters."
            required
          >
            <PasswordInput
              name="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
            />
          </Field>

          {!unitId ? (
            <p className="alert alert--error">
              This link is missing which unit to apply for. Contact the management office for a
              valid link.
            </p>
          ) : null}

          <button className="button" type="submit" disabled={busy || !unitId}>
            {busy ? 'Creating your account…' : 'Continue'}
          </button>
        </form>
      </div>
    </section>
  );
}
